import { type ReactNode, useEffect, useState } from 'react';
import { debounce, noop } from 'lodash';
import styled from 'styled-components';
import {
  CopyCommentsOptions,
  MaxAssetCountPerBulkAction,
} from '@frameio/assets/operations';
import { plural } from '@frameio/i18n';
import { useFeatureGatePlanSuggestion } from '@frameio/plan-management/feature-gate/hooks';
import {
  Box,
  Button,
  Menu,
  MenuRadio,
  MenuRadioGroup,
  Separator,
  Sheet,
  Text,
  theme,
  Toggle,
  ToggleCheckbox,
  Tooltip,
} from '@frameio/vapor';
import { IconChevronDown, IconX } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/MoveToCopyToSheet';

export enum moveType {
  MOVE_TO = 'move_to',
  COPY_TO = 'copy_to',
}

type SheetProps = {
  canSubmit: boolean;
  copyComments?: boolean;
  children?: ReactNode;
  isShowing: boolean;
  method?: moveType;
  onCopyCommentsChange?: (value: boolean) => void;
  onRequestClose: () => void;
  onSubmit: () => void;
  selectedCount: number;
  enableTooltip?: boolean;
  changingWorkspaces: boolean;
  onDropdownSelection: (value: any) => void;
  dropdownValue: CopyCommentsOptions;
};

const SHEET_HEIGHT = 510; // pixels

TEXT.DEFINE({
  internalComments: 'Internal comments',
  publicComments: 'Public comments',
  allComments: 'All comments',
  /** Shorter label for private comments */
  private: 'Internal',
  /** Shorter label for public comments */
  public: 'Public',
  /** Shorter label for all comments */
  all: 'All',
  moveTo: 'Move to',
  copyTo: 'Copy to',
  copyComments: 'Also copy comments',
  includeComments: 'Include comments',
  label: 'move to copy to sheet',

  copyButton: (count: number) =>
    plural(count, `Copy ${count} Items`, 'Copy 1 Item'),
  moveButton: (count: number) =>
    plural(count, `Move ${count} Items`, 'Move 1 Item'),

  maxAssetCountTooltipText: (maxAssetCount: number) =>
    `Please select ${maxAssetCount} assets or fewer to perform this action.`,
});

/**
 * The MoveToCopyToSheet renders a vapor Sheet component with a container for a
 * FolderTree and a button that changes text based on whether a move or copy
 * will be performed.
 *
 * @param canSubmit can the submit button be pressed?
 * @param copyComments is the copy comments checkbox toggled?
 * @param isShowing whether or not to show the `Sheet`
 * @param method can be `move_to` or `copy_to` and determines the text to show
 * @param onCopyCommentsChange callback for when the copyComments checkbox is toggled
 * @param onRequestClose callback for when the close icon is clicked
 * @param onSubmit callback for when the move/copy is submitted
 * @param selectedCount number of items that will be moved/copied
 */
export function MoveToCopyToSheet({
  canSubmit,
  children,
  copyComments = false,
  isShowing,
  method = moveType.MOVE_TO,
  onCopyCommentsChange = noop,
  onRequestClose,
  onSubmit,
  selectedCount,
  enableTooltip = false,
  changingWorkspaces,
  onDropdownSelection,
  dropdownValue,
}: SheetProps) {
  const [isBreakpointBase, setIsBreakpointBase] = useState(false);
  const title = method === moveType.COPY_TO ? TEXT.copyTo : TEXT.moveTo;

  const featureGatePlanSuggestion =
    useFeatureGatePlanSuggestion('TEAM_ONLY_COMMENTS');

  const teamOnlyCommentsFeatureAvailable =
    featureGatePlanSuggestion?.includedInCurrentPlan;

  // Copy to sheet: always show copy comments and checkbox with dropdown
  // Move to sheet: show only copy comments and only when moving between workspaces
  const showCommentSection = method === moveType.COPY_TO || changingWorkspaces;
  const showInternalCommentsMoveCopyOptions =
    teamOnlyCommentsFeatureAvailable &&
    (changingWorkspaces || method === moveType.COPY_TO);

  /**
   * Inspired by `C2CSheetProperties`
   * This is needed to ensure the Sheet is not opened on tablet or
   * desktop sizes. We are using a ResizeObserver so that it works
   * with RSC as well.
   */
  useEffect(() => {
    const resizeObserver = new ResizeObserver(
      debounce((entries) => {
        const newValue = entries[0].contentRect;

        setIsBreakpointBase(
          newValue.width < parseInt(theme.breakpoints.tablet, 10)
        );
      }, 100)
    );

    resizeObserver.observe(document.body);

    return () => {
      resizeObserver.unobserve(document.body);
    };
  }, []);

  const disabledTooltipCount =
    method === moveType.MOVE_TO
      ? MaxAssetCountPerBulkAction.move
      : MaxAssetCountPerBulkAction.copy;

  let copyCommentsLabel = '';

  switch (dropdownValue) {
    case CopyCommentsOptions.ALL:
      copyCommentsLabel = TEXT.all;
      break;
    case CopyCommentsOptions.PRIVATE:
      copyCommentsLabel = TEXT.private;
      break;
    case CopyCommentsOptions.PUBLIC:
      copyCommentsLabel = TEXT.public;
      break;
    default:
      copyCommentsLabel = '';
  }

  return (
    <Sheet
      aria-label={TEXT.label}
      closeIcon={<IconX />}
      data-testid="project-nav--move-to-copy-to-sheet"
      id="move_to_copy_to__sheet"
      isShowing={isShowing}
      margin={0}
      /*
       * Negative offset, ensures that the sheet's bottom bar is
       * correctly aligned with the bottom bar on the asset grid
       */
      offset={-1}
      onRequestClose={onRequestClose}
      placement="bottom"
      shouldUseOverlay={false}
      /**
       * The Sheet should take the whole width on mobile web.
       */
      shouldUsePortal={isBreakpointBase}
      size={SHEET_HEIGHT}
      style={{
        width: '100%',
      }}
      title={title}
    >
      <TreeContainer>{children}</TreeContainer>

      <Box width="100%">
        {showCommentSection && (
          <Wrapper>
            <Separator gap={0} />
            {showInternalCommentsMoveCopyOptions ? (
              <Box
                alignItems="center"
                display="flex"
                justifyContent="space-between"
                paddingX={16}
                paddingY={12}
              >
                {method === moveType.COPY_TO ? (
                  <Toggle
                    checked={copyComments}
                    onChange={() => onCopyCommentsChange(!copyComments)}
                  >
                    <ToggleCheckbox />

                    <Text
                      color="secondary"
                      style={{ paddingLeft: CHECKBOX_LABEL_PADDING }}
                      variant="body"
                    >
                      {TEXT.includeComments}
                    </Text>
                  </Toggle>
                ) : (
                  <Text
                    color="secondary"
                    style={{ paddingLeft: CHECKBOX_LABEL_PADDING }}
                    variant="body"
                  >
                    {TEXT.includeComments}
                  </Text>
                )}

                <Menu
                  content={
                    <>
                      <MenuRadioGroup
                        checked={dropdownValue}
                        onChange={onDropdownSelection}
                      >
                        <MenuRadio name={CopyCommentsOptions.ALL}>
                          {TEXT.allComments}
                        </MenuRadio>
                      </MenuRadioGroup>
                      <MenuRadioGroup
                        checked={dropdownValue}
                        onChange={onDropdownSelection}
                      >
                        <MenuRadio name={CopyCommentsOptions.PUBLIC}>
                          {TEXT.publicComments}
                        </MenuRadio>

                        {method === moveType.COPY_TO && (
                          <MenuRadio name={CopyCommentsOptions.PRIVATE}>
                            {TEXT.internalComments}
                          </MenuRadio>
                        )}
                      </MenuRadioGroup>
                    </>
                  }
                >
                  <Button
                    disabled={!copyComments && method !== moveType.MOVE_TO}
                    iconAfter={<IconChevronDown size={16} />}
                    size="small"
                    variant="ghost"
                  >
                    {copyCommentsLabel}
                  </Button>
                </Menu>
              </Box>
            ) : (
              <Box paddingX={16} paddingY={12}>
                <Toggle
                  checked={copyComments}
                  onChange={() => onCopyCommentsChange(!copyComments)}
                >
                  <ToggleCheckbox />

                  <Text
                    color="secondary"
                    style={{ paddingLeft: CHECKBOX_LABEL_PADDING }}
                    variant="body"
                  >
                    {TEXT.copyComments}
                  </Text>
                </Toggle>
              </Box>
            )}
          </Wrapper>
        )}

        <Wrapper>
          <Separator gap={0} />

          <Box paddingX={16} paddingY={12}>
            <Tooltip
              disabled={!enableTooltip}
              title={TEXT.maxAssetCountTooltipText(disabledTooltipCount)}
            >
              <span>
                <Button
                  block={true}
                  color="primary"
                  disabled={!canSubmit}
                  onClick={onSubmit}
                  size="small"
                >
                  {method === moveType.COPY_TO
                    ? TEXT.copyButton(selectedCount)
                    : TEXT.moveButton(selectedCount)}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Wrapper>
      </Box>
    </Sheet>
  );
}

const CHECKBOX_LABEL_PADDING = 8;

const Wrapper = styled.div`
  /* Background is required so on toggle additional menu items do not overlap */
  background: ${theme.colors.components.sheet.background};
  position: relative;
`;

const TreeContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;
